import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { peptideProducts, merchandiseProducts } from '../../mocks/products';
import { todaysTopFive } from '../../mocks/top-five';

export default function HomePage() {
  const featuredProducts = [...peptideProducts.slice(0, 3), ...merchandiseProducts.slice(0, 3)];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_68852d3832d441f8bf852eb99a500cfa_dae8e4bf.jpg"
            alt="Premium gym interior with professional equipment and moody lighting"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-display font-bold text-5xl md:text-7xl lg:text-8xl text-white mb-6 leading-tight">
              Curate Your<br />
              <span className="text-electric-blue">Peak Performance</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
              Premium peptides, elite fitness regimens, curated playlists, and investment strategies for the modern optimizer
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/peptides"
                className="px-8 py-4 bg-electric-blue text-white rounded-lg text-base font-semibold hover:bg-blue-500 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Shop Premium Peptides
              </Link>
              <Link
                to="/fitness"
                className="px-8 py-4 bg-white text-navy rounded-lg text-base font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Fitness Workouts &amp; Programs
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-8 h-8 flex items-center justify-center"
          >
            <i className="ri-arrow-down-line text-3xl text-white/70"></i>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display font-bold text-4xl md:text-5xl text-navy mb-4">
              Why Mixtape Curated?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to optimize performance, recovery, and lifestyle in one curated platform
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-flask-line',
                title: 'Certified Peptides',
                description: 'Premium quality peptides for recovery, performance, and wellness optimization',
              },
              {
                icon: 'ri-fitness-line',
                title: 'Daily Workouts',
                description: 'Featured exercises, assistance lifts, and meal plans updated every day',
              },
              {
                icon: 'ri-music-line',
                title: 'Curated Playlists',
                description: 'Perfect soundtracks for lifting, running, and recovery sessions',
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Investment Strategies',
                description: 'M&A methods and proven investment approaches for wealth building',
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-baby-blue/20 to-white p-8 rounded-2xl border border-baby-blue/30 hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-electric-blue rounded-xl mb-4">
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="font-display font-bold text-xl text-navy mb-3">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-baby-blue/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display font-bold text-4xl md:text-5xl text-navy mb-4">
              Featured Products
            </h2>
            <p className="text-lg text-gray-600">
              Premium peptides and Sentinel Brands merchandise
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group cursor-pointer"
              >
                <div className="relative h-72 bg-gradient-to-br from-baby-blue/20 to-white overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain p-8 group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 px-3 py-1 bg-electric-blue text-white text-xs font-semibold rounded-full">
                    {product.category}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display font-bold text-xl text-navy mb-2">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-navy">${product.price}</span>
                    <button className="px-6 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer">
                      Add to Cart
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/shop"
              className="inline-block px-8 py-4 bg-navy text-white rounded-lg text-base font-semibold hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display font-bold text-4xl md:text-5xl text-navy mb-4">
              Today's Top Five
            </h2>
            <p className="text-lg text-gray-600">
              {todaysTopFive.date} • {todaysTopFive.category}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-6">
            {todaysTopFive.items.map((item, index) => (
              <motion.div
                key={item.rank}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-6 p-6 bg-gradient-to-r from-baby-blue/20 to-white rounded-2xl border border-baby-blue/30 hover:shadow-lg transition-all"
              >
                <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-electric-blue text-white rounded-xl font-display font-bold text-2xl">
                  {item.rank}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${item.icon} text-2xl text-navy`}></i>
                    </div>
                    <h3 className="font-display font-bold text-xl text-navy">{item.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/top-five"
              className="inline-block px-8 py-4 bg-electric-blue text-white rounded-lg text-base font-semibold hover:bg-blue-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Top Five Lists
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-navy via-navy-dark to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display font-bold text-4xl md:text-5xl mb-6">
                Ready to Optimize Your Performance?
              </h2>
              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                Book a consultation to discuss peptide protocols, personalized training programs, nutrition strategies, and investment opportunities tailored to your goals.
              </p>
              <Link
                to="/booking"
                className="inline-block px-8 py-4 bg-electric-blue text-white rounded-lg text-base font-semibold hover:bg-blue-500 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Book Your Consultation
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              {[
                { number: '500+', label: 'Satisfied Clients' },
                { number: '50+', label: 'Premium Products' },
                { number: '365', label: 'Daily Updates' },
                { number: '24/7', label: 'Support Available' },
              ].map((stat, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 text-center"
                >
                  <div className="font-display font-bold text-4xl text-electric-blue mb-2">
                    {stat.number}
                  </div>
                  <div className="text-sm text-white/80">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}